// Architecture-tree system (P0) — SPEC_TECH_TREE_PIVOT.md §4, grounded in
// docs/graphics_history.md §5. A DAG of capability nodes per scenario; research
// accrues engineer-months exactly like tools/yield research (drawn ahead of
// projects), completes with a breakthrough GRADE roll, banks pivot credits
// toward sibling approaches, and carries authored obsolescence riders (the
// world standardizing against your bet). Garage-stage research reuses the same
// completion path with founder-months (garage.ts calls treeAccrue directly).

import type { AttrVector, ResearchGrade, TechNodeDef, TechNodeId, TechTreeDef } from "../types.js";
import { ATTR_KEYS, DISCIPLINES } from "../types.js";
import type { TickContext } from "../ctx.js";
import { charge, emit } from "../ctx.js";
import { interpolate } from "../calendar.js";

/** The scenario's tree (P0: one tree per campaign; empty def when absent). */
export function activeTree(ctx: TickContext): TechTreeDef | null {
  const id = ctx.content.scenario.treeId;
  if (!id) return null;
  return ctx.content.trees.find((t) => t.id === id) ?? null;
}

export function getTechNode(tree: TechTreeDef, id: TechNodeId): TechNodeDef {
  const n = tree.nodes.find((x) => x.id === id);
  if (!n) throw new Error(`unknown tech node ${id}`);
  return n;
}

export function isResearched(ctx: TickContext, id: TechNodeId): boolean {
  return id in ctx.s.tree.researched;
}

export function prereqsMet(ctx: TickContext, node: TechNodeDef): boolean {
  return node.prereqs.every((p) => isResearched(ctx, p));
}

/** Effective EM cost: soft-prereq discounts, then the soft-exclusivity penalty
 *  (×penalty while a researched sibling holds the group), minus banked credits. */
export function effectiveEmCost(ctx: TickContext, node: TechNodeDef): number {
  let cost = node.emCost;
  for (const sp of node.softPrereqs ?? []) {
    if (isResearched(ctx, sp.id)) cost *= 1 - sp.discount;
  }
  if (node.exclusiveGroup) {
    const tree = activeTree(ctx);
    const siblingHeld = tree?.nodes.some(
      (n) => n.id !== node.id && n.exclusiveGroup === node.exclusiveGroup && isResearched(ctx, n.id),
    );
    if (siblingHeld) cost *= ctx.content.economy.tree.exclusivePenaltyMult;
  }
  return Math.max(1, cost - (ctx.s.tree.credits[node.id] ?? 0));
}

/** Grade multiplier applied to a node's attr contribution. */
export function gradeMult(ctx: TickContext, grade: ResearchGrade): number {
  return ctx.content.economy.tree.gradeAttrMult[grade];
}

/** A node's attr contribution at month m (obsolescence riders applied). */
export function nodeAttrsAt(node: TechNodeDef, m: number): Partial<AttrVector> {
  if (!node.attrs) return {};
  const factor = node.obsolescence && m >= node.obsolescence.month ? node.obsolescence.factor : 1;
  if (factor === 1) return node.attrs;
  const out: Partial<AttrVector> = {};
  for (const k of ATTR_KEYS) {
    const v = node.attrs[k];
    if (v !== undefined) out[k] = v * factor;
  }
  return out;
}

/** The tree FRONTIER at month m: the attrs of an imaginary company that
 *  researched (grade B) everything the world has had for frontierLagMonths.
 *  Within an exclusiveGroup only the best member counts per dimension — the
 *  frontier picks the winning bet, it doesn't hold four theses at once. */
export function frontierAttrs(ctx: TickContext, tree: TechTreeDef, m: number): AttrVector {
  const lag = ctx.content.economy.tree.frontierLagMonths;
  const out: AttrVector = { perf3d: 0, perf2d: 0, features: 0, compat: 0, efficiency: 0 };
  const groupBest: Record<string, Partial<AttrVector>> = {};
  for (const node of tree.nodes) {
    if (node.availableMonth > m - lag) continue;
    const attrs = nodeAttrsAt(node, m);
    if (!node.exclusiveGroup) {
      for (const k of ATTR_KEYS) out[k] += attrs[k] ?? 0;
    } else {
      const best = (groupBest[node.exclusiveGroup] ??= {});
      for (const k of ATTR_KEYS) best[k] = Math.max(best[k] ?? 0, attrs[k] ?? 0);
    }
  }
  for (const best of Object.values(groupBest)) {
    for (const k of ATTR_KEYS) out[k] += best[k] ?? 0;
  }
  return out;
}

/** Roll a completion grade. Weights shift toward A with a technologist founder
 *  still aboard (garage-originated advantage that persists — the person does). */
export function rollGrade(ctx: TickContext, node: TechNodeDef): ResearchGrade {
  const cfg = ctx.content.economy.tree;
  let [a, b, c] = node.gradeWeights ?? cfg.gradeWeights;
  // technologist shift: applies while the founding team (which includes any
  // technologist) is aboard — in the garage or after incorporation
  const foundingTeam = ctx.s.garage?.founders ?? (ctx.s.garageLegacy ? ctx.content.scenario.garage?.founders : undefined);
  if (foundingTeam?.some((f) => f.archetype === "technologist")) {
    const shift = Math.min(c, ctx.content.economy.garage.technologistGradeShift);
    a += shift;
    c -= shift;
  }
  const total = a + b + c;
  const roll = ctx.rng.uniform() * total;
  return roll < a ? "A" : roll < a + b ? "B" : "C";
}

/** Bank EM into a node; completes it (grade roll, credits, cash cost, events)
 *  when the effective cost is met. Shared by company research and the garage. */
export function treeAccrue(ctx: TickContext, node: TechNodeDef, em: number): void {
  const t = ctx.s.tree;
  t.inProgress[node.id] = (t.inProgress[node.id] ?? 0) + em;
  const needed = effectiveEmCost(ctx, node);
  if (t.inProgress[node.id]! < needed) return;

  // completion
  delete t.inProgress[node.id];
  const grade = rollGrade(ctx, node);
  t.researched[node.id] = { grade, month: ctx.s.monthIndex };
  delete t.staffing[node.id];
  // pivot salvage (spec §4.4): the work wasn't worthless, the bet was wrong
  for (const cr of node.creditsTo ?? []) {
    t.credits[cr.id] = (t.credits[cr.id] ?? 0) + node.emCost * cr.frac;
  }
  // one-time cash cost lands at completion (e.g. the emulator purchase);
  // in the garage it draws the pooled savings instead of company cash
  if (node.cashCost) {
    const cost = Math.round(interpolate(node.cashCost, ctx.s.monthIndex));
    if (cost > 0) {
      if (ctx.s.stage === "garage" && ctx.s.garage) {
        ctx.s.garage.savings = Math.max(0, ctx.s.garage.savings - cost);
      } else {
        charge(ctx, "treeCash", -cost, `${node.displayName} — capital purchase`);
      }
    }
  }
  emit(ctx, "treeResearched", `${node.displayName} — research complete (grade ${grade})`, {
    nodeId: node.id,
    grade,
    tier: node.tier,
  });
}

/** Company-phase research: staffed engineers draw EM ahead of projects, split
 *  by the node's discipline mix — the same scarce-heads bite as tools/yield. */
export function processTree(ctx: TickContext): void {
  const { s } = ctx;
  if (s.dead) return;
  const tree = activeTree(ctx);
  if (!tree) return;
  const m = s.monthIndex;

  for (const nodeId of Object.keys(s.tree.staffing).sort()) {
    const staff = s.tree.staffing[nodeId]!;
    if (staff <= 0) continue;
    const node = getTechNode(tree, nodeId);
    if (isResearched(ctx, nodeId)) {
      delete s.tree.staffing[nodeId];
      continue;
    }
    if (node.availableMonth > m || !prereqsMet(ctx, node)) continue; // staff idle until the world/prereqs catch up
    let em = 0;
    for (const d of DISCIPLINES) {
      const want = staff * (node.disciplineMix[d] ?? 0) * ctx.monthsPerTick;
      const draw = Math.min(want, ctx.emBudget[d]);
      ctx.emBudget[d] -= draw;
      ctx.allocation[d].tree += draw;
      em += draw;
    }
    if (em > 0) treeAccrue(ctx, node, em);
  }

  // authored obsolescence riders: the world standardized against your bet
  for (const node of tree.nodes) {
    if (node.obsolescence && node.obsolescence.month === m && isResearched(ctx, node.id)) {
      emit(ctx, "treeObsoleted", `${node.obsolescence.headline} — ${node.displayName} is suddenly last-generation.`, {
        nodeId: node.id,
        factor: node.obsolescence.factor,
        headline: node.obsolescence.headline,
        body: node.obsolescence.body,
      });
    }
  }
}
